//#region Imports
import { useEffect, useState } from 'react';
import { faTimes } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Button,
  Skeleton,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  TextField,
  Typography,
} from '@mui/material';

import { GlobalVariables } from '../../../../globalVar';
import customAxios from '../../../../utils/customAxios';
import { getToast } from '../../../../utils/toastConfig';
//#endregion

const HEADERS = {
  headers: {
    Authorization: 'bearer ' + localStorage.getItem('token'),
  },
};

/**
 * Component for displaying a modal to update an existing region's details.
 * @param {Object} props - The component props.
 * @param {number} props.regionId - The ID of the failure type to update.
 * @param {boolean} props.showModal - Indicates whether the modal is open.
 * @param {function} props.setShowModal - Function to toggle the modal's visibility.
 * @param {function} props.refreshData - Function to refresh the regions data after updating.
 * @returns {JSX.Element} The `UpdateRegionModal` component.
 */
export const UpdateFailureTypeModal = ({
  failureTypeId,
  showModal,
  setShowModal,
  refreshData,
}) => {
  const [isLoading, setIsLoading] = useState(false); // State for tracking submission process
  const [isFetching, setIsFetching] = useState(false); // State for tracking data fetching
  const [failureType, setFailureType] = useState({ name: '', description: '' }); // State for region details

  useEffect(() => {
    if (showModal) {
      fetchData(); // Fetch region details when the modal opens
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showModal]);

  /**
   * Fetches the details of the selected region.
   */
  const fetchData = async () => {
    setIsFetching(true);
    const endpoint = `${GlobalVariables.getURL()}failure-types/${failureTypeId}`;

    try {
      const response = await customAxios.get(endpoint, HEADERS);
      const failureType = response.data.data;
      setFailureType({
        name: failureType.name,
        description: failureType.description,
      }); // Update failure type state with fetched details
    } catch (error) {
      console.error('Error fetching failure types details:', error);
      getToast({
        status: 'error',
        title: error.response?.data?.message || 'Error',
      });
    } finally {
      setIsFetching(false);
    }
  };

  /**
   * Resets the form fields to their initial state.
   */
  const handleResetFields = () => {
    setFailureType({ name: '', description: '' });
  };
  /**
   * Closes the modal and resets the form fields.
   */
  const handleClose = () => {
    setShowModal(false);
    handleResetFields();
    refreshData(); // Refresh failure types data
  };

  /**
   * Handles the form submission to create a new failure type.
   * @param {Event} e - The form submission event.
   */
  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    const endpoint = `${GlobalVariables.getURL()}failure-types/update/${failureTypeId}`;

    try {
      const response = await customAxios.patch(endpoint, failureType, {
        headers: {
          Authorization: 'bearer ' + localStorage.getItem('token'),
        },
      });

      if (response.status === 200) {
        getToast({
          status: 'success',
          title: 'Tipo de servicio actualizado exitosamente',
        }); // Show success message
        handleClose(); // Close modal and reset fields
      }
    } catch (error) {
      console.error('Error creating failure type:', error);
      getToast({
        status: 'error',
        title: error.response?.data?.message || 'Error',
      }); // Show error message
    } finally {
      setIsLoading(false); // Reset loading state
    }
  };

  return (
    <Dialog open={showModal} maxWidth='md' fullWidth onClose={handleClose}>
      <DialogTitle>
        <Typography className='inventory-movmodal-title'>
          Editar tipo de servicio
        </Typography>
      </DialogTitle>
      <IconButton
        aria-label='close'
        onClick={handleClose}
        sx={{
          position: 'absolute',
          right: 20,
          top: 20,
          width: 40,
          height: 40,
        }}
      >
        <FontAwesomeIcon icon={faTimes} />
      </IconButton>
      <DialogContent dividers>
        <form id='new-region-form' onSubmit={handleSubmit}>
          <Grid container spacing={2}>
            {/* Name Field */}
            <Grid item xs={12} md={6}>
              {isFetching ? (
                <Skeleton variant='rectangular' width='100%' height={56} />
              ) : (
                <TextField
                  fullWidth
                  disabled={isLoading}
                  label='Nombre'
                  placeholder='Nombre...'
                  type='text'
                  value={failureType.name}
                  onChange={(e) =>
                    setFailureType((prev) => ({
                      ...prev,
                      name: e.target.value,
                    }))
                  }
                />
              )}
            </Grid>
            {/* Description Field */}
            <Grid item xs={12} md={6}>
              {isFetching ? (
                <Skeleton variant='rectangular' width='100%' height={56} />
              ) : (
                <TextField
                  fullWidth
                  disabled={isLoading}
                  label='Descripción'
                  placeholder='Descripción...'
                  type='text'
                  value={failureType.description}
                  onChange={(e) =>
                    setFailureType((prev) => ({
                      ...prev,
                      description: e.target.value,
                    }))
                  }
                />
              )}
            </Grid>
          </Grid>
        </form>
      </DialogContent>
      <DialogActions>
        <Button
          variant='contained'
          form='new-region-form'
          type='submit'
          disabled={isLoading}
        >
          {isLoading ? (
            <CircularProgress size='2rem' />
          ) : (
            <Typography>Guardar</Typography>
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
